import dayjs, { type Dayjs } from '@/utilities/dayjs-config'

import type {
  AvailableMeals,
  Campaign,
  DeliveryCycle,
  DeliveryZone,
  ExceptionDelivery,
} from '@/types/pops'
import type { ValidExceptionDelivery } from '@/types/components/program-panel/delivery-window'
import { EARLIEST_NEW_PROGRAM_WEEK } from '@/constants/program-dates'

type OBJECT_DELIVERED_MEALS = Partial<Record<AvailableMeals, number>>

const getMealsDeliveredOnDay = (
  delivery: DeliveryCycle,
  meals: AvailableMeals[],
) => {
  const mealsOnDay: OBJECT_DELIVERED_MEALS = {}
  meals.forEach((meal) => {
    if (meal === 'breakfast')
      mealsOnDay[meal] = delivery.deliveries.filter((meal) =>
        meal.startsWith('B'),
      ).length
    else if (meal === 'lunch')
      mealsOnDay[meal] = delivery.deliveries.filter((meal) =>
        meal.startsWith('L'),
      ).length
    else if (meal === 'dinner')
      mealsOnDay[meal] = delivery.deliveries.filter((meal) =>
        meal.startsWith('D'),
      ).length
  })
  return mealsOnDay
}

const getNumberOfMealsDelivered = (meals: OBJECT_DELIVERED_MEALS) => {
  return Object.values(meals).reduce((rtn, cur) => ((rtn += cur), rtn), 0)
}

const reduceMealsRequired = (
  mealsRequired: OBJECT_DELIVERED_MEALS,
  mealsDelivered: OBJECT_DELIVERED_MEALS,
) => {
  return Object.fromEntries(
    Object.entries(mealsRequired).map(([meal, count]) => {
      const newCount = count - (mealsDelivered[meal as AvailableMeals] || 0)
      return [meal, newCount >= 0 ? newCount : 0]
    }),
  ) as OBJECT_DELIVERED_MEALS
}

export const getProgramStartDay = (deliveries: DeliveryCycle[]) => {
  const day = Number(deliveries[0].deliveries[0].replace(/\D+/g, ''))
  return day === 7 ? 0 : day
}

const getExceptionDelivery = (exceptions: ExceptionDelivery[], week: Dayjs) => {
  for (let i = 0; i < exceptions.length; i++) {
    const exception = exceptions[i]
    if (week.isSame(exception.week, 'week')) return exception
  }
  return undefined
}

export const isProgramStartDateOOS = (oosDates: string[], date: Dayjs) => {
  for (const oosDate of oosDates) {
    if (date.isSame(oosDate, 'date')) return true
  }
  return false
}

export const getDeliveryTimeString = (times: string[]) => {
  const startTime = dayjs(`1970-01-01 ${times[0]}`, 'YYYY-MM-DD HH:mm')
  const endTime = dayjs(`1970-01-01 ${times[1]}`, 'YYYY-MM-DD HH:mm')

  const isSameTimes = startTime.format('A') === endTime.format('A')

  return `${startTime.format(isSameTimes ? 'h' : 'hA')}-${endTime.format('hA')}`
}

const getDeliveries = (
  deliveryCycles: DeliveryCycle[],
  meals: AvailableMeals[],
  daysOfMeals: number,
  firstDeliveryDay: string,
  exception?: ExceptionDelivery,
) => {
  const isException = !!exception
  const deliveries: DeliveryCycle[] = []
  let includedFirstDeliveryDay = false
  if (isException) {
    const shouldSkipFromException =
      daysOfMeals < (exception.minimum_deliverable_meals || 0)
    if (shouldSkipFromException)
      return { deliveries: [], daysOfMealsDelivered: 0, isException }
    deliveryCycles = exception.delivery_cycles

    firstDeliveryDay =
      exception.days_of_food_first_delivery_days[daysOfMeals]?.[0] ||
      exception.delivery_cycles[0].day
  }
  let mealsRequired = meals.reduce(
    (rtn, key) => ((rtn[key] = daysOfMeals), rtn),
    {} as OBJECT_DELIVERED_MEALS,
  )
  let mealsRemains = getNumberOfMealsDelivered(mealsRequired)
  for (let i = 0; i < deliveryCycles.length; i++) {
    const delivery = deliveryCycles[i]
    if (delivery.day !== firstDeliveryDay && !includedFirstDeliveryDay) continue
    if (delivery.day === firstDeliveryDay) includedFirstDeliveryDay = true
    const mealsDelivered = getMealsDeliveredOnDay(delivery, meals)
    if (getNumberOfMealsDelivered(mealsDelivered) > 0) deliveries.push(delivery)
    mealsRequired = reduceMealsRequired(mealsRequired, mealsDelivered)
    mealsRemains = getNumberOfMealsDelivered(mealsRequired)
    if (mealsRemains === 0) break
  }
  const daysOfMealsDelivered =
    daysOfMeals - Math.min(...Object.values(mealsRequired))
  if (mealsRemains !== 0 && !isException)
    return {
      deliveries: [],
      daysOfMealsDelivered,
      isException,
    }
  return {
    deliveries,
    daysOfMealsDelivered,
    isException,
  }
}

const filterLiveCampaign = (campaign: Campaign) => {
  const res = dayjs().tz().isSameOrAfter(dayjs.tz(campaign.go_live_date), 'day')
  return res
}

const filterDayjsAfterDayjs = (compareDay: Dayjs) => (day: Dayjs) => {
  return day.isSameOrAfter(compareDay, 'day')
}

const filterCleanseCampaign = (campaign: Campaign) => {
  return campaign.cleanse_campaign === true
}

const getCampaignProgramStartDates = (
  campaignId: number | undefined,
  campaignIdentifier: undefined | string,
  futureCampaigns: Campaign[],
) => {
  let campaigns: Campaign[] = []
  if (campaignId || campaignIdentifier) {
    campaigns = futureCampaigns
      .filter(
        (campaign) =>
          campaign.id === campaignId ||
          campaign.identifier == campaignIdentifier,
      )
      .filter(filterLiveCampaign)
  } else {
    campaigns = futureCampaigns
      .filter(filterCleanseCampaign)
      .filter(filterLiveCampaign)
  }
  const campaignWeeks = campaigns.reduce((dates, campaign) => {
    campaign.start_weeks.forEach((date) => dates.add(date))
    campaign.sold_out_start_weeks.forEach((date) => dates.delete(date))
    return dates
  }, new Set<string>())
  const availableWeeks = [...campaignWeeks]
    .map((date) => dayjs(date))
    .sort((a, b) => (a.isSameOrAfter(b) ? 1 : -1))
  const filterDateAfterNewProgramStartWeek = filterDayjsAfterDayjs(
    EARLIEST_NEW_PROGRAM_WEEK,
  )
  return availableWeeks.filter(filterDateAfterNewProgramStartWeek)
}

export const getAvailableProgramStartDates = (
  newProgramWeekCap: number,
  programType: string,
  {
    campaignId = undefined,
    campaignIdentifier = undefined,
    futureCampaigns = undefined,
  }: {
    campaignId?: number
    campaignIdentifier?: undefined | string
    futureCampaigns?: Campaign[]
  },
) => {
  if (programType === 'campaign' && Array.isArray(futureCampaigns)) {
    return getCampaignProgramStartDates(
      campaignId,
      campaignIdentifier,
      futureCampaigns,
    )
  }

  return Array.from({ length: newProgramWeekCap }).map((_, i) =>
    EARLIEST_NEW_PROGRAM_WEEK.add(i, 'week').startOf('week'),
  )
}

const _getDeliverySlot = (
  meals: AvailableMeals[],
  currentStartWeek: Dayjs,
  daysOfMeal: number,
  deliveryZone: DeliveryZone,
  isAllowException: boolean,
) => {
  const { days_of_food_first_delivery_days, delivery_cycles, exceptions } =
    deliveryZone
  const exceptionDelivery = getExceptionDelivery(exceptions, currentStartWeek)
  const firstDeliveryDay = days_of_food_first_delivery_days[daysOfMeal][0]
  const { daysOfMealsDelivered, deliveries, isException } = getDeliveries(
    delivery_cycles,
    meals,
    daysOfMeal,
    firstDeliveryDay,
    exceptionDelivery,
  )
  if (!deliveries.length) return null
  let exception
  if (isException && exceptionDelivery) {
    if (!isAllowException) return null
    exception = {
      week: exceptionDelivery.week,
      daysOfMealsDelivered,
      deliveries,
    }
  }

  return {
    deliveries,
    isException,
    exception,
  }
}

export const getDeliverySlot = (
  programLength: number,
  meals: AvailableMeals[],
  currentStartWeek: Dayjs,
  daysOfMeal: number,
  deliveryZone: DeliveryZone,
  isAllowException: boolean,
) => {
  let firstDeliveries
  const exceptionDeliveries: ValidExceptionDelivery[] = []
  if (!daysOfMeal) return null
  for (let i = 0; i < programLength; i++) {
    const startWeek = dayjs(currentStartWeek).add(i, 'week').startOf('week')
    const deliverySlot = _getDeliverySlot(
      meals,
      startWeek,
      daysOfMeal,
      deliveryZone,
      isAllowException,
    )
    if (!deliverySlot) return null
    const { deliveries, isException, exception } = deliverySlot
    if (!firstDeliveries && (!isException || programLength - 1 === i))
      firstDeliveries = [...deliveries]
    if (isException && exception) exceptionDeliveries.push(exception)
  }
  if (!firstDeliveries) return null
  return {
    deliveries: firstDeliveries,
    isException: exceptionDeliveries.length > 0,
    exceptionDeliveries,
  }
}
